/**
 * Générateur — Automatisme : reconnaitre un cube, un pavé représenté en
 * perspective cavalière.
 * Attendu ID : 1068 | Niveau : 5ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Cube ou pavé ?                (QCM nom le plus précis ; planche ①-④ ; V/F sur quatre solides)
 *   - Lire la perspective           (QCM pointillés / vraie grandeur / fuyantes / nombre de pointillés / inclusion)
 *   - Cube ou pavé : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°30 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUIT au CM1, CONSOLIDÉ au CM2 (nommer, décrire, pointillés),
 * automatisme d'identification des sept solides en 6e, automatisme ciblé
 * cube / pavé en 5e ; la construction de la perspective est FORMALISÉE en 5e
 * (objectif, hors gen). P2 : sens installés — nommer, décrire par les faces ;
 * 6e — identifier sur une représentation ; non installés — les propriétés de
 * la perspective (face de devant en vraie grandeur, fuyantes en
 * parallélogrammes). Distracteurs : « des parallélogrammes donc pas un cube »,
 * « une face carrée suffit », prisme droit pris pour un pavé, pyramide.
 * P3 : 6 faces, 12 arêtes, 8 sommets, 3 arêtes cachées. P5 : perspective
 * cavalière avec pointillés, vues canoniques (moteur commun svgCavaliere.js :
 * cube à face avant carrée, pavé non cube à face avant non carrée, sinon
 * indiscernables sur un dessin). P6 : aucun instrument. P7 : pas de
 * construction, pas de patron, pas de mesure sur le dessin, pas de volume.
 * P8 : cube, pavé, face, arête, sommet, arête cachée, perspective
 * cavalière ; prisme droit, pyramide, cylindre, cône, boule (noms de 6e).
 * P9 : identification des sept solides et nature des faces restent implicites.
 * P10 : constat (« lequel », « quel », « combien »). P11 : les faces fuyantes
 * d'un cube sont dessinées en parallélogrammes ; une face carrée ne fait pas
 * un cube ; un cube EST un pavé (inclusion). P12 : rémanence forte.
 * INVARIANTS :
 *   - la nature d'un solide dessiné se RELIT sur ses primitives (face avant
 *     rectangle axé carré ou non, triangles, ellipse, arcs, cercle) : aucun
 *     attribut ne la donne ; les planches n'ont jamais deux solides de même
 *     nature ; verrou d'inclusion : cible « pavé non cube » → jamais de
 *     cube dans la planche ; jamais de prétention « prisme droit » en V/F
 *     (cube et pavé en sont) ;
 *   - QCM : « le nom LE PLUS PRÉCIS » désamorce l'inclusion cube ⊂ pavé ;
 *   - V/F : jamais de crochets, mixité 1-3 faux, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';
import { SOLIDES, poserSolide, svgSolide, BLEU } from './svgCavaliere';

const ATTENDU_ID = 1068;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE = 'Cube ou pavé ?';
const L_LECTURE = 'Lire la perspective';
const L_VF = 'Cube ou pavé : vrai ou faux ?';

const NUMS = ['①', '②', '③', '④'];
const TYPES = ['cube', 'pave', 'prisme', 'pyramide', 'cylindre', 'cone', 'boule'];
const NOMS = { cube: 'un cube', pave: 'un pavé', prisme: 'un prisme droit', pyramide: 'une pyramide', cylindre: 'un cylindre', cone: 'un cône', boule: 'une boule' };
const NOM_COURT = { cube: 'Un cube', pave: 'Un pavé', prisme: 'Un prisme droit', pyramide: 'Une pyramide', cylindre: 'Un cylindre', cone: 'Un cône', boule: 'Une boule' };

/** Planche 2 × 2 de solides numérotés (panneaux 130 × 120). */
const svgPlanche = (types, extra = '') => {
    const PW = 130; const PH = 120;
    let s = '';
    types.forEach((type, i) => {
        const ox = (i % 2) * PW; const oy = Math.floor(i / 2) * PH;
        s += `<g data-index="${i + 1}">`
            + `<rect x="${ox + 1}" y="${oy + 1}" width="${PW - 2}" height="${PH - 2}" fill="#fff" stroke="#e2e8f0"/>`
            + `<text x="${ox + 8}" y="${oy + 18}" font-size="15" font-weight="800" fill="${BLEU}">${NUMS[i]}</text>`
            + poserSolide(SOLIDES[type](), ox + PW / 2 + 6, oy + PH / 2 + 8, PW - 36, PH - 34).join('')
            + '</g>';
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${2 * PW} ${2 * PH}" width="${2 * PW}" height="${2 * PH}" font-family="Arial"${extra}>${s}</svg>`;
};

// ─── Carte 1a : le nom le plus précis (QCM) ────────────────────────────────
// Le dessin est TOUJOURS un cube ou un pavé non cube (décision prof 2026-09-05 : la réponse attendue
// reste dans l'attendu ; les autres solides ne servent que de leurres).

// Options : toujours « cube » ET « pavé » (la confusion visée) plus deux noms tirés parmi les cinq
// autres solides, même distribution pour les deux types ; type à pile ou face sans alternance
// (revue 2026-09-05, A2 : les leurres fixes par type et l'alternance stricte trahissaient la réponse).
const AUTRES_NOMS = ['prisme', 'pyramide', 'cylindre', 'cone', 'boule'];

const genNatureQcm = () => {
    const type = randomChoice(['cube', 'pave']);
    const options = shuffle(['cube', 'pave', ...shuffle(AUTRES_NOMS).slice(0, 2)].map((t) => NOM_COURT[t]));
    const svg = svgSolide(SOLIDES[type]());
    const payload = makeQcm(options, NOM_COURT[type], '<p>Ce solide est représenté en perspective cavalière, arêtes cachées en pointillés.</p><p>Quel est le nom <strong>le plus précis</strong> de ce solide ?</p>', {
        ...opts(L_NATURE), elements_additionnels: imgElement(svg, 'small'),
    });
    payload.contenu = { ...payload.contenu, sousType: type };
    payload._meta.params = { type, signature: type };
    return payload;
};

// ─── Carte 1b : planche ①-④ (QCM) ─────────────────────────────────────────

let derniereSignaturePlanche1068 = null;

const genPlancheQcm = () => {
    let payload = construirePlancheQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignaturePlanche1068; t++) payload = construirePlancheQcm();
    derniereSignaturePlanche1068 = payload._meta.params.signature;
    return payload;
};

const construirePlancheQcm = () => {
    const cible = randomChoice(['cube', 'pave']);
    // verrou d'inclusion : cible « pavé non cube » → pas de cube dans la planche ;
    // cible « cube » → le pavé est TOUJOURS là, c'est le leurre de la fiche (revue 2026-09-05, A3)
    const autres = TYPES.filter((t) => t !== cible && t !== 'cube' && t !== 'pave');
    const imposes = cible === 'cube' ? ['pave'] : [];
    const types = shuffle([cible, ...imposes, ...shuffle(autres).slice(0, 3 - imposes.length)]);
    const bonneIndex = types.indexOf(cible);
    const options = NUMS.map((n) => `Solide ${n}`);
    const question = cible === 'cube' ? 'Lequel de ces solides est un <strong>cube</strong> ?' : 'Lequel de ces solides est un <strong>pavé</strong> qui n\'est pas un cube ?';
    const svg = svgPlanche(types);
    const payload = makeQcm(options, options[bonneIndex], `<p>Quatre solides sont représentés en perspective cavalière, arêtes cachées en pointillés.</p><p>${question}</p>`, {
        ...opts(L_NATURE), elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType: cible };
    payload._meta.params = { cible, types, bonneIndex, signature: `${cible}-${types.join(',')}` };
    return payload;
};

// ─── Carte 1c : V/F sur quatre solides ─────────────────────────────────────
// Prétentions LIMITÉES à « cube » et « pavé » (décision prof 2026-09-05 : on reconnaît le cube et le
// pavé, les autres solides de la planche ne sont que des leurres visuels). « Pavé » est vrai pour un
// cube aussi (inclusion) ; jamais « prisme droit » (cube et pavé en sont).

const PRETENTIONS = ['cube', 'pave'];
const estUn = (type, pretendu) => (pretendu === 'pave' ? type === 'cube' || type === 'pave' : type === pretendu);

let dernieresLignesVf1068 = [];

const genNatureVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        // Une ligne vraie exige un cube ou un pavé (au plus 2 par planche, solides distincts) : 2 ou 3 faux.
        // La planche contient donc le cube et le pavé (2 faux) ou l'un des deux (3 faux), plus des intrus.
        const nbFaux = randomChoice([2, 2, 3]);
        const cibles = nbFaux === 2 ? ['cube', 'pave'] : [randomChoice(['cube', 'pave'])];
        const intrus = shuffle(TYPES.filter((t) => t !== 'cube' && t !== 'pave')).slice(0, 4 - cibles.length);
        const types = shuffle([...cibles, ...intrus]);
        const lignes = types.map((type, i) => {
            const vrais = PRETENTIONS.filter((p) => estUn(type, p)); const faux = PRETENTIONS.filter((p) => !estUn(type, p));
            // cube ou pavé : le pavé peut recevoir la prétention fausse « cube » ; le cube n'a que des prétentions vraies
            let pretendu;
            if (vrais.length && (type === 'cube' || Math.random() < 0.7)) pretendu = randomChoice(vrais);
            else pretendu = randomChoice(faux.length ? faux : vrais);
            return { l: `Le solide ${NUMS[i]} est ${NOMS[pretendu]}.`, v: estUn(type, pretendu), pretendu };
        });
        const faux = lignes.filter((it) => !it.v).length;
        if (faux < 1 || faux > 3) continue;
        // fenêtre : jamais la même planche (types + prétentions) que le tirage précédent
        const sig = `${types.join(',')}|${lignes.map((it) => it.pretendu).join(',')}`;
        if (essai > 120) dernieresLignesVf1068 = [];
        if (dernieresLignesVf1068.includes(sig)) continue;
        dernieresLignesVf1068 = [sig];
        const svg = svgPlanche(types);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Quatre solides sont représentés en perspective cavalière, arêtes cachées en pointillés. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_NATURE), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { types, pretendus: lignes.map((it) => it.pretendu) };
        return payload;
    }
    throw new Error('gen_1068 : V/F solides sans tirage après 160 essais');
};

// ─── Carte 2a : lire la perspective (QCM) ──────────────────────────────────

// Longueurs des options homogénéisées (revue 2026-09-05, A2 : la bonne était la plus longue à 60 %).
const QUESTIONS_LECTURE = [
    {
        id: 'pointilles',
        q: 'Sur ce dessin, que représentent les traits en pointillés ?',
        bonne: 'Les arêtes cachées, invisibles sur un solide qui serait opaque.',
        leurres: ['Les arêtes les plus courtes du solide, dessinées plus fines.', 'Les arêtes de la face de devant, dessinées en vraie grandeur.', 'Les arêtes qu\'il faudrait mesurer pour connaître le volume.'],
    },
    {
        id: 'vraie_grandeur',
        q: 'Sur ce dessin en perspective cavalière, quelle face est dessinée en vraie grandeur ?',
        bonne: 'La face de devant, seulement elle.',
        leurres: ['La face du dessus, seulement elle.', 'Les faces fuyantes, celles qui partent vers l\'arrière.', 'Toutes les faces, c\'est le principe de la perspective.'],
    },
    {
        id: 'fuyantes',
        q: 'Sur ce dessin, les faces qui partent vers l\'arrière (les faces fuyantes) sont des rectangles. Comment sont-elles dessinées ?',
        bonne: 'Comme des parallélogrammes, sans angles droits sur le dessin.',
        leurres: ['Comme des rectangles en vraie grandeur, avec leurs angles droits.', 'Comme des trapèzes, plus étroits en allant vers l\'arrière.', 'Comme des triangles, puisqu\'on n\'en voit que la moitié.'],
    },
    {
        id: 'nb_pointilles',
        q: 'Combien d\'arêtes sont dessinées en pointillés sur ce dessin ?',
        bonne: '3 arêtes : celles issues du sommet caché, à l\'arrière.',
        leurres: ['2 arêtes : celles du fond, cachées par la face avant.', '4 arêtes : les quatre arêtes de la face arrière.', '6 arêtes : la moitié des douze arêtes du solide.'],
    },
    {
        id: 'inclusion',
        q: 'Ce solide est un cube. Est-il aussi un pavé ?',
        bonne: 'Oui : c\'est un pavé dont les trois dimensions sont égales.',
        leurres: ['Non : un cube a des faces carrées, un pavé des faces rectangulaires.', 'Non : un pavé a plus de faces qu\'un cube.', 'Oui : tous les pavés sont des cubes.'],
    },
];

let derniereQuestionLecture1068 = null;

const genLectureQcm = () => {
    let i = randInt(0, QUESTIONS_LECTURE.length - 1);
    if (i === derniereQuestionLecture1068) i = (i + 1) % QUESTIONS_LECTURE.length;
    derniereQuestionLecture1068 = i;
    const Q = QUESTIONS_LECTURE[i];
    const type = Q.id === 'inclusion' ? 'cube' : randomChoice(['cube', 'pave']);
    const options = shuffle([Q.bonne, ...Q.leurres]);
    const svg = svgSolide(SOLIDES[type]());
    const payload = makeQcm(options, Q.bonne, `<p>${Q.q}</p>`, { ...opts(L_LECTURE), elements_additionnels: imgElement(svg, 'small') });
    payload.contenu = { ...payload.contenu, sousType: Q.id };
    payload._meta.params = { question: Q.id, type, signature: `${Q.id}-${type}` };
    return payload;
};

// (La saisie faces / arêtes / sommets a été retirée : connaissances sur le solide, pas sur sa
//  représentation — décision prof 2026-09-05 ; la question des pointillés est passée dans le QCM.)

// ─── Carte 3 : vrai ou faux ? ──────────────────────────────────────────────

const POOL_VF = [
    { l: 'Un cube est un pavé particulier : ses trois dimensions sont égales.', v: true },
    { l: 'Un cube a 6 faces carrées, 12 arêtes et 8 sommets.', v: true },
    { l: 'Sur une perspective cavalière, les faces fuyantes d\'un cube sont dessinées comme des parallélogrammes.', v: true },
    { l: 'Sur une perspective cavalière, seule la face de devant est dessinée en vraie grandeur.', v: true },
    { l: 'Les traits en pointillés représentent les arêtes cachées du solide.', v: true },
    { l: 'Un pavé peut avoir des faces carrées sans être un cube.', v: true },
    { l: 'Un pavé est toujours un cube.', v: false },
    { l: 'Sur une perspective cavalière, on peut mesurer la longueur de toutes les arêtes sur le dessin.', v: false },
    { l: 'Si une face du dessin est un carré, alors le solide est forcément un cube.', v: false },
    { l: 'Si deux faces sont dessinées comme des parallélogrammes, alors le solide n\'est pas un cube.', v: false },
    { l: 'Toutes les arêtes d\'un pavé ont la même longueur.', v: false },
    { l: 'Un pavé a 8 arêtes et 12 sommets.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1068 : pool V/F déséquilibré');
POOL_VF.forEach((it) => { if (/[[\]]/.test(it.l)) throw new Error('gen_1068 : crochets dans une ligne V/F'); });

// Paires vrai / faux sur le même fait : jamais servies ensemble (revue 2026-09-05, A8 : 47 % des tableaux).
const FAMILLE_VF_1068 = (l) => {
    if (/cube est un pavé particulier|pavé est toujours un cube/.test(l)) return 'inclusion';
    if (/faces carrées sans être|face du dessin est un carré/.test(l)) return 'face_carree';
    if (/parallélogrammes/.test(l)) return 'parallelogrammes';
    if (/vraie grandeur|mesurer la longueur/.test(l)) return 'mesure';
    if (/12 arêtes et 8 sommets|8 arêtes et 12 sommets/.test(l)) return 'comptes';
    return l;
};

let derniersServisVf1068 = [];

const genVf = () => {
    for (let essai = 0; essai < 120; essai++) {
        const dispo = POOL_VF.filter((it) => !derniersServisVf1068.includes(it.l));
        const tirage = shuffle(dispo).slice(0, 4);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        if (new Set(tirage.map((it) => FAMILLE_VF_1068(it.l))).size !== 4) continue;
        derniersServisVf1068 = tirage.map((it) => it.l);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Cube et pavé en perspective cavalière. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1068 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genNatureQcm,
    nature_planche_qcm_unique: genPlancheQcm,
    nature_tableau_vf: genNatureVf,
    lecture_qcm_unique: genLectureQcm,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', planche_qcm_unique: 'Planche (QCM)', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'lecture', label: L_LECTURE, formatsList: buildFormats('lecture') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
